import { useState, useEffect, useRef } from 'react';
import { Calendar, ChevronDown, Check } from 'lucide-react';

const MONTHS = [
  'Janeiro', 'Fevereiro', 'Março', 'Abril', 'Maio', 'Junho',
  'Julho', 'Agosto', 'Setembro', 'Outubro', 'Novembro', 'Dezembro',
];

interface MonthSelectorProps {
  /** Índice do mês selecionado (0-11) */
  value: number;
  onChange: (monthIndex: number) => void;
  year?: number;
  /** Quando true, aplica estilo sobreposto a fundo mint */
  onMint?: boolean;
}

/**
 * Dropdown de seleção de mês/ano.
 * Fecha automaticamente ao clicar fora (mousedown listener com cleanup).
 * Touch targets: botão trigger 44px mín, cada item 44px.
 */
export function MonthSelector({ value, onChange, year = 2026, onMint = false }: MonthSelectorProps) {
  const [open, setOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    function handleMouseDown(e: MouseEvent) {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    }
    document.addEventListener('mousedown', handleMouseDown);
    return () => document.removeEventListener('mousedown', handleMouseDown);
  }, [open]);

  const trigBg = onMint ? 'rgba(31,58,46,0.10)' : 'var(--card)';
  const trigColor = onMint ? 'var(--on-mint)' : 'var(--fg)';
  const trigBorder = onMint
    ? '1px solid rgba(255,255,255,0.35)'
    : '1px solid var(--card-stroke)';

  return (
    <div ref={containerRef} style={{ position: 'relative' }}>
      <button
        onClick={() => setOpen((o) => !o)}
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-label={`Mês selecionado: ${MONTHS[value]} ${year}. Clique para alterar`}
        style={{
          width: '100%',
          minHeight: 44,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 10,
          padding: '10px 14px',
          borderRadius: 'var(--r-pill)',
          background: trigBg,
          color: trigColor,
          border: trigBorder,
          cursor: 'pointer',
          fontFamily: 'var(--font-sans)',
        }}
      >
        <span style={{ display: 'flex', alignItems: 'center', gap: 9 }}>
          <Calendar size={15} strokeWidth={1.8} aria-hidden />
          <span style={{ fontSize: 13, fontWeight: 600 }}>
            {MONTHS[value]} {year}
          </span>
        </span>
        <ChevronDown
          size={15}
          strokeWidth={1.8}
          aria-hidden
          style={{
            transform: open ? 'rotate(180deg)' : 'none',
            transition: 'transform var(--t-fast)',
            opacity: 0.8,
          }}
        />
      </button>

      {open && (
        <div
          role="listbox"
          aria-label="Selecionar mês"
          style={{
            position: 'absolute',
            top: 'calc(100% + 6px)',
            left: 0,
            right: 0,
            zIndex: 30,
            maxHeight: 248,
            overflowY: 'auto',
            padding: 6,
            background: 'var(--card)',
            border: '1px solid var(--card-stroke)',
            borderRadius: 'var(--r-3)',
            boxShadow: 'var(--shadow-2)',
          }}
        >
          {MONTHS.map((month, i) => (
            <button
              key={i}
              role="option"
              aria-selected={i === value}
              onClick={() => {
                onChange(i);
                setOpen(false);
              }}
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                width: '100%',
                minHeight: 44,
                padding: '9px 12px',
                borderRadius: 'var(--r-2)',
                fontSize: 13,
                fontWeight: i === value ? 600 : 400,
                color: i === value ? 'var(--mint-deep)' : 'var(--fg)',
                background: i === value ? 'var(--mint-soft)' : 'transparent',
                cursor: 'pointer',
                fontFamily: 'var(--font-sans)',
                textAlign: 'left',
              }}
            >
              <span>{month} {year}</span>
              {i === value && <Check size={14} strokeWidth={2} aria-hidden />}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
